
import React, { useState } from 'react';
import { X, Upload, Check, AlertTriangle, FileText, ArrowRight, Loader2, Download } from 'lucide-react';
import { ImportService, ImportPreviewRow, ImportError } from '../../services/ImportService';
import { MappingManager } from './MappingManager';
import { User, AssetCategory, Supplier, CostCenter } from '../../types';

interface AssetImportModalProps {
    isOpen: boolean;
    onClose: () => void;
    user: User;
    existingCategories: AssetCategory[];
    existingSuppliers: Supplier[];
    existingCostCenters: CostCenter[];
    existingBranches: User[]; // Added
    existingTeam: User[]; // Added
    onSuccess: () => void;
}

type ImportStep = 'upload' | 'map' | 'validate' | 'result';

export const AssetImportModal: React.FC<AssetImportModalProps> = ({
    isOpen, onClose, user, existingCategories, existingSuppliers, existingCostCenters, existingBranches, existingTeam, onSuccess
}) => {
    const [step, setStep] = useState<ImportStep>('upload');
    const [file, setFile] = useState<File | null>(null);
    const [fileHeaders, setFileHeaders] = useState<string[]>([]);
    const [rawData, setRawData] = useState<any[]>([]);
    const [mapping, setMapping] = useState<Record<string, string | string[]>>({});

    // Validation State
    const [validationResult, setValidationResult] = useState<{
        preview: ImportPreviewRow[];
        errors: ImportError[];
        newEntities: { categories: any[], suppliers: any[], costCenters: any[] };
        updates?: any[];
    } | null>(null);

    // Execution State
    const [isExecuting, setIsExecuting] = useState(false);
    const [importSummary, setImportSummary] = useState<any>(null);
    const [updateMode, setUpdateMode] = useState(false); // Default: Skip
    const [splitByQuantity, setSplitByQuantity] = useState(false); // Default: Don't split

    // Progress State
    const [progress, setProgress] = useState<{ value: number; message: string; phase: 'validating' | 'importing' } | null>(null);

    const agencyId = user.agencyId || user.id;

    React.useEffect(() => {
        if (isOpen) {
            setStep('upload');
            setFile(null);
            setFileHeaders([]);
            setRawData([]);
            setMapping({});
            setValidationResult(null);
            setImportSummary(null);
            setUpdateMode(false);
            setUpdateMode(false);
            setIsExecuting(false);
            setProgress(null);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const f = e.target.files[0];
            setFile(f);
            try {
                const data = await ImportService.parseFile(f);
                setRawData(data);
                if (data.length > 0) {
                    setFileHeaders(Object.keys(data[0]));
                    setStep('map');
                } else {
                    alert("File appears to be empty");
                }
            } catch (err) {
                console.error("Parse Error", err);
                alert("Failed to parse file");
            }
        }
    };

    const handleValidation = async () => {
        if (!file || Object.keys(mapping).length === 0) return;

        // Ensure current user is in the team list for email resolution
        const allTeam = [...(existingTeam || [])];
        if (!allTeam.find(u => u.id === user.id)) {
            allTeam.push(user);
        }

        try {
            setProgress({ value: 0, message: "Starting validation...", phase: 'validating' });

            const res = await ImportService.validateAndResolve(
                rawData,
                mapping,
                agencyId,
                existingCategories,
                existingSuppliers,
                existingCostCenters,
                existingBranches,
                allTeam, // Pass augmented team
                (status, pct) => {
                    setProgress({ value: pct, message: status, phase: 'validating' });
                }
            );
            setValidationResult(res);
            setStep('validate');
            setTimeout(() => setProgress(null), 500); // Clear progress after short delay
        } catch (error) {
            console.error("Validation Error", error);
            alert("Validation failed");
            setProgress(null);
        }
    };

    const handleExecute = async () => {
        if (!validationResult) return;
        setIsExecuting(true);
        setProgress({ value: 0, message: "Initializing Import...", phase: 'importing' });

        try {
            // 1. Create Entities
            const entityRes = await ImportService.executeImport(
                validationResult.preview,
                validationResult.newEntities,
                agencyId,
                undefined, // resolvedMaps
                (status, pct) => setProgress({ value: pct, message: status, phase: 'importing' })
            );

            // Re-build "Maps" for the batch import.
            // Ideally validationResult would return the "Resolved ID" for each row.
            // For this quick MVP, we will assume the Service can lookup by name again or we refresh.
            // Let's proceed with a simpler approach: Service returns the MAPS.

            // We need to fetch ALL entities again to build a complete map?
            // Or just update the client side maps?
            // To be safe, let's inject a "Resolved Map" helper in the Service execution.
            // I modified Service to return 'maps' in executeImport. 
            // BUT wait, I need to pass *existing* maps merged with new ones.

            const norm = (s: string) => s?.toString().trim().toLowerCase() || '';
            const catMap = new Map(existingCategories.map(c => [norm(c.title), c.id]));
            const supMap = new Map(existingSuppliers.map(s => [norm(s.name), s.id]));
            const ccMap = new Map(existingCostCenters.map(c => [norm(c.code), c.id]));

            // Merge new IDs from entityRes if possible. 
            // Actually, ImportService.executeImport returns `maps` which are likely JUST newly created. 
            if (entityRes.maps) {
                entityRes.maps.catIdMap.forEach((v: string, k: string) => catMap.set(k, v));
                entityRes.maps.supIdMap.forEach((v: string, k: string) => supMap.set(k, v));
                entityRes.maps.ccIdMap.forEach((v: string, k: string) => ccMap.set(k, v));
            }

            // 2. Batch Import Assets
            const validRows = validationResult.preview.filter(r => !r.__errors__);
            const totalToImport = validRows.length;

            setProgress({ value: 0, message: `Importing assets (0/${totalToImport})...`, phase: 'importing' });

            const assetRes = await ImportService.batchImportAssets(
                validRows,
                agencyId,
                { catIdMap: catMap, supIdMap: supMap, ccIdMap: ccMap },
                updateMode,
                splitByQuantity,
                (processed, total) => {
                    const pct = Math.round((processed / total) * 100);
                    setProgress({
                        value: pct,
                        message: `Importing assets (${processed}/${total})...`,
                        phase: 'importing'
                    });
                }
            );

            setImportSummary({
                total: rawData.length,
                success: assetRes.success,
                failed: assetRes.failed + (validationResult.errors.length > 0 ? 0 : 0), // Errors in valid rows vs invalid rows?
                // Actually parse errors are separate.
                createdEntities: entityRes
            });
            setStep('result');
            onSuccess();
        } catch (e) {
            console.error(e);
            alert("Import execution failed");
        } finally {
            setIsExecuting(false);
            setProgress(null);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                    <h2 className="text-xl font-bold text-slate-800">Import Assets</h2>
                    <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full text-slate-500">
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {step === 'upload' && (
                        <div className="flex flex-col items-center justify-center h-full space-y-4 py-12 border-2 border-dashed border-slate-300 rounded-xl bg-slate-50">
                            <Upload size={48} className="text-slate-400" />
                            <div className="text-center">
                                <p className="text-lg font-bold text-slate-700">Drop your CSV or Excel file here</p>
                                <p className="text-sm text-slate-500">or click to browse</p>
                            </div>
                            <input
                                type="file"
                                accept=".csv, .xlsx, .xls"
                                onChange={handleFileUpload}
                                className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 max-w-xs mx-auto"
                            />
                        </div>
                    )}

                    {step === 'map' && (
                        <MappingManager
                            fileHeaders={fileHeaders}
                            currentMapping={mapping}
                            onMappingChange={setMapping}
                            agencyId={agencyId}
                        />
                    )}

                    {/* Progress Bar Overlay */}
                    {progress && (
                        <div className="absolute inset-0 bg-white/80 backdrop-blur-[1px] z-10 flex flex-col items-center justify-center p-8">
                            <div className="w-full max-w-md space-y-4">
                                <div className="flex justify-between text-sm font-medium text-slate-700">
                                    <span>{progress.message}</span>
                                    <span>{progress.value}%</span>
                                </div>
                                <div className="h-3 bg-slate-100 rounded-full overflow-hidden border border-slate-200">
                                    <div
                                        className="h-full bg-blue-600 transition-all duration-300 ease-out rounded-full shadow-[0_0_10px_rgba(37,99,235,0.3)]"
                                        style={{ width: `${Math.max(2, progress.value)}%` }}
                                    />
                                </div>
                                <div className="flex justify-center">
                                    <Loader2 className="animate-spin text-blue-600" size={24} />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Updates Prompt */}
                    {(step === 'validate' && validationResult?.updates && validationResult.updates.length > 0) && (
                        <div className="bg-blue-50 p-4 rounded-xl border border-blue-200 mb-6">
                            <div className="flex items-center gap-2 text-blue-800 font-bold mb-2">
                                <AlertTriangle size={18} />
                                Existing Assets Found
                            </div>
                            <p className="text-sm text-blue-700 mb-3">
                                Found {validationResult.updates.length} assets with barcodes that already exist in the system.
                            </p>
                            <div className="flex bg-white rounded-lg p-1 border border-blue-100 w-fit">
                                <button
                                    onClick={() => setUpdateMode(false)}
                                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${!updateMode ? 'bg-blue-100 text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                                        }`}
                                >
                                    Skip Existing
                                </button>
                                <button
                                    onClick={() => setUpdateMode(true)}
                                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${updateMode ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
                                        }`}
                                >
                                    Update Existing
                                </button>
                            </div>
                            <p className="text-xs text-blue-600 mt-2">
                                {updateMode
                                    ? "Existing assets will be updated with values from this CSV."
                                    : "Rows with existing barcodes will be ignored."}
                            </p>
                        </div>
                    )}

                    {step === 'validate' && validationResult && (
                        <div className="space-y-6">
                            {/* Asset Splitting Option */}
                            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex items-center justify-between">
                                <div>
                                    <div className="font-bold text-slate-800 text-sm">Asset Splitting</div>
                                    <p className="text-xs text-slate-500">
                                        Should assets with Quantity {'>'} 1 be split into individual rows?
                                    </p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        checked={splitByQuantity}
                                        onChange={e => setSplitByQuantity(e.target.checked)}
                                    />
                                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                    <span className="ml-3 text-sm font-medium text-slate-900">{splitByQuantity ? 'Yes, Split' : 'No, Keep Grouped'}</span>
                                </label>
                            </div>

                            {/* Summary Cards */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="p-4 bg-blue-50 rounded-xl">
                                    <div className="text-2xl font-bold text-blue-700">{validationResult.preview.length}</div>
                                    <div className="text-xs font-bold text-blue-400 uppercase">Total Rows</div>
                                </div>
                                <div className="p-4 bg-green-50 rounded-xl">
                                    <div className="text-2xl font-bold text-green-700">
                                        {validationResult.preview.length - validationResult.errors.length}
                                    </div>
                                    <div className="text-xs font-bold text-green-400 uppercase">Valid Rows</div>
                                </div>
                                <div className="p-4 bg-red-50 rounded-xl">
                                    <div className="text-2xl font-bold text-red-700">{validationResult.errors.length}</div>
                                    <div className="text-xs font-bold text-red-400 uppercase">Errors</div>
                                </div>
                                <div className="p-4 bg-purple-50 rounded-xl">
                                    <div className="text-2xl font-bold text-purple-700">
                                        {validationResult.newEntities.categories.length +
                                            validationResult.newEntities.suppliers.length +
                                            validationResult.newEntities.costCenters.length}
                                    </div>
                                    <div className="text-xs font-bold text-purple-400 uppercase">New Entities</div>
                                </div>
                            </div>

                            {/* New Entities Preview */}
                            {(validationResult.newEntities.categories.length > 0 ||
                                validationResult.newEntities.suppliers.length > 0) && (
                                    <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-200">
                                        <div className="flex items-center gap-2 text-yellow-800 font-bold mb-2">
                                            <AlertTriangle size={18} />
                                            New Entities will be created:
                                        </div>
                                        <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1">
                                            {validationResult.newEntities.categories.length > 0 && (
                                                <li>{validationResult.newEntities.categories.length} new Categories</li>
                                            )}
                                            {validationResult.newEntities.suppliers.length > 0 && (
                                                <li>{validationResult.newEntities.suppliers.length} new Suppliers</li>
                                            )}
                                            {validationResult.newEntities.costCenters.length > 0 && (
                                                <li>{validationResult.newEntities.costCenters.length} new Cost Centers</li>
                                            )}
                                        </ul>
                                    </div>
                                )}

                            {/* Error Table */}
                            {validationResult.errors.length > 0 && (
                                <div className="border border-red-200 rounded-xl overflow-hidden">
                                    <div className="bg-red-100 p-2 font-bold text-red-800 text-sm">Validation Errors</div>
                                    <div className="max-h-60 overflow-y-auto">
                                        <table className="w-full text-sm text-left">
                                            <thead className="bg-red-50 text-red-900">
                                                <tr>
                                                    <th className="p-2">Row</th>
                                                    <th className="p-2">Error</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {validationResult.errors.slice(0, 50).map((err, idx) => (
                                                    <tr key={idx} className="border-b border-red-100">
                                                        <td className="p-2 font-mono text-xs">{err.row}</td>
                                                        <td className="p-2 text-red-600">{err.message}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {validationResult.errors.length > 50 && (
                                            <div className="p-2 text-center text-xs text-slate-500">
                                                ...and {validationResult.errors.length - 50} more errors
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {step === 'result' && importSummary && (
                        <div className="flex flex-col items-center justify-center p-8 text-center space-y-6">
                            <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center">
                                <Check size={32} />
                            </div>
                            <div>
                                <h3 className="text-2xl font-bold text-slate-800">Import Completed!</h3>
                                <p className="text-slate-500">
                                    Successfully processed {importSummary.success} assets.
                                </p>
                            </div>
                            <div className="w-full max-w-sm bg-slate-50 p-6 rounded-2xl border border-slate-200">
                                <div className="flex justify-between py-2 border-b border-slate-200">
                                    <span className="text-slate-600">Total Processed</span>
                                    <span className="font-bold">{importSummary.total}</span>
                                </div>
                                <div className="flex justify-between py-2 border-b border-slate-200">
                                    <span className="text-green-600">Success</span>
                                    <span className="font-bold text-green-600">{importSummary.success}</span>
                                </div>
                                <div className="flex justify-between py-2">
                                    <span className="text-red-600">Failed</span>
                                    <span className="font-bold text-red-600">{importSummary.failed}</span>
                                </div>
                            </div>
                            <button onClick={onClose} className="btn-primary w-full max-w-xs">
                                Close
                            </button>
                        </div>
                    )}
                </div>

                {/* Footer / Actions */}
                <div className="p-4 border-t border-slate-100 flex justify-between bg-white">
                    {step === 'upload' && (
                        <div /> // Spacer
                    )}

                    {step === 'map' && (
                        <>
                            <button onClick={() => setStep('upload')} className="text-slate-500 hover:text-slate-700 font-medium">
                                Back
                            </button>
                            <button
                                onClick={handleValidation}
                                disabled={Object.keys(mapping).length === 0}
                                className="bg-slate-900 text-white px-6 py-2 rounded-xl font-bold hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2"
                            >
                                Validate <ArrowRight size={18} />
                            </button>
                        </>
                    )}

                    {step === 'validate' && (
                        <>
                            <button onClick={() => setStep('map')} className="text-slate-500 hover:text-slate-700 font-medium">
                                Back to Mapping
                            </button>
                            <button
                                onClick={handleExecute}
                                disabled={isExecuting || validationResult!.preview.length === validationResult!.errors.length} // Disable if ALL failed
                                className="bg-green-600 text-white px-6 py-2 rounded-xl font-bold hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                            >
                                {isExecuting ? <Loader2 className="animate-spin" size={18} /> : <Check size={18} />}
                                {isExecuting ? 'Importing...' : 'Start Import'}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
